import React, {useState, useEffect} from 'react';
import TextInput from 'components/inputs/TextInput';
import DateInput from 'components/inputs/DateInput';
import {TextBlockElementClass} from 'types/documentTemplateElement';
import {User} from 'types/user';
import {Location} from 'types/location';
import {JobRole} from 'types/jobRole';
import CustomSelect from 'components/inputs/CustomSelect';
import {UserService} from '../../services/userService';
import {LocationService} from '../../services/locationService';
import {JobRoleService} from '../../services/jobRoleService';
import {SearchType} from '../../types/search';

interface DynamicTextBlockInputsProps {
  elements: TextBlockElementClass[];
  onUpdateElement: (tagName: string, content: string) => void;
  user: User;
  filterSupervisorsByRole?: boolean;
  selectedJobRole?: JobRole;
  onUpdateAllInputsFilled: (allInputsFilled: boolean) => void;
}

const titleize = (text: string): string => {
  return text
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getDefaultValue = (tagName: string, user: User): string => {
  switch (tagName) {
    case 'EMPLOYEE_FIRST_NAME':
      return user.firstName || '';
    case 'EMPLOYEE_LAST_NAME':
      return user.lastName || '';
    case 'EMPLOYEE_FULL_NAME':
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    case 'EMPLOYEE_SUPERVISOR':
      return user.supervisors?.[0]?.firstName
        ? `${user.supervisors[0].firstName} ${user.supervisors[0].lastName}`.trim()
        : '';
    case 'EMPLOYEE_LOCATION':
      return user.locations?.[0]?.name || '';
    case 'COMPANY_NAME':
      return '';
    case 'EMPLOYEE_ROLE':
      return user.jobRoles?.[0]?.name || '';
    case 'CURRENT_DATE':
      return new Date().toLocaleDateString();
    case 'COMPENSATION':
      return '';
    default:
      return '';
  }
};

const DynamicTextBlockInputs: React.FC<DynamicTextBlockInputsProps> = ({
  elements,
  onUpdateElement,
  user,
  filterSupervisorsByRole = false,
  selectedJobRole,
  onUpdateAllInputsFilled,
}) => {
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [tagOptions, setTagOptions] = useState<
    Record<string, Array<{value: string; label: string}>>
  >({});

  useEffect(() => {
    const initialValues: Record<string, string> = {};
    const updates: Array<{tagName: string; value: string}> = [];

    elements.forEach(element => {
      const content = element.properties.content || '';
      const tags = content.match(/tag-value="\{([^}]+)\}"/g) || [];

      tags.forEach(tag => {
        const tagMatch = tag.match(/tag-value="\{([^}]+)\}"/);
        const tagName = tagMatch ? tagMatch[1] : '';
        const inputId = `${element.id}_${tagName}`;
        const defaultValue = getDefaultValue(tagName, user);
        initialValues[inputId] = defaultValue;
        if (defaultValue) {
          updates.push({tagName, value: defaultValue});
        }
      });
    });

    setInputValues(initialValues);

    updates.forEach(update => {
      onUpdateElement(update.tagName, update.value);
    });
  }, [elements, user]);

  useEffect(() => {
    const loadTagOptions = async () => {
      const options: Record<string, Array<{value: string; label: string}>> = {};

      for (const element of elements) {
        const content = element.properties.content || '';
        const tags = content.match(/tag-value="\{([^}]+)\}"/g) || [];

        for (const tag of tags) {
          const tagMatch = tag.match(/tag-value="\{([^}]+)\}"/);
          const tagName = tagMatch ? tagMatch[1] : '';

          switch (tagName) {
            case 'EMPLOYEE_LOCATION':
              const locationSearch: SearchType<{[key: string]: any}> = {
                where: {
                  is_active: {
                    operator: '==',
                    value: 'true',
                  },
                },
              };
              const locationResponse = await LocationService.index(
                1,
                locationSearch,
                '',
                '',
                1000,
              );
              options[tagName] = locationResponse.data.map(
                (location: Location) => ({
                  value: location.name,
                  label: location.name,
                }),
              );
              break;
            case 'EMPLOYEE_SUPERVISOR':
              if (filterSupervisorsByRole && selectedJobRole) {
                options[tagName] =
                  selectedJobRole.supervisors?.map((user: User) => ({
                    value: user.name,
                    label: user.name,
                  })) || [];
                if (options[tagName].length > 0) {
                  break;
                }
              }
              const supervisorSearch: SearchType<{[key: string]: any}> = {
                where: {
                  is_supervisor: {
                    operator: '==',
                    value: 'true',
                  },
                },
              };
              const supervisorResponse = await UserService.index(
                1,
                supervisorSearch,
                '',
                '',
                1000,
              );
              options[tagName] = supervisorResponse.data.map((user: User) => ({
                value: user.name,
                label: user.name,
              }));

              break;
            case 'EMPLOYEE_ROLE':
              const jobRoleSearch: SearchType<{[key: string]: any}> = {
                where: {
                  is_active: {
                    operator: '==',
                    value: 'true',
                  },
                },
              };
              const jobRoleResponse = await JobRoleService.index(
                1,
                jobRoleSearch,
                '',
                '',
                1000,
              );
              options[tagName] = jobRoleResponse.data.map((role: JobRole) => ({
                value: role.name,
                label: role.name,
              }));
              break;
          }
        }
      }
      setTagOptions(options);
    };
    loadTagOptions();
  }, [elements, user]);

  // Update validation logic to handle cases where there are no inputs
  useEffect(() => {
    const allInputsFilled =
      elements.length === 0 ||
      elements.every(element => {
        const content = element.properties.content || '';
        const tags = content.match(/tag-value="\{([^}]+)\}"/g) || [];

        return tags.every(tag => {
          const tagMatch = tag.match(/tag-value="\{([^}]+)\}"/);
          const tagName = tagMatch ? tagMatch[1] : '';
          const inputId = `${element.id}_${tagName}`;
          return inputValues[inputId]?.trim() !== '';
        });
      });

    onUpdateAllInputsFilled(allInputsFilled);
  }, [elements, inputValues]);

  const handleChange = (
    elementId: string,
    tagName: string,
    value: string | {value: string; label: string},
  ) => {
    const inputId = `${elementId}_${tagName}`;
    const newValue = typeof value === 'string' ? value : value.value;

    setInputValues(prev => ({
      ...prev,
      [inputId]: newValue,
    }));
    onUpdateElement(tagName, newValue);
  };

  const renderInput = (tagName: string, elementId: string) => {
    if (tagName === 'CURRENT_DATE') {
      return renderDateInput(tagName, elementId);
    } else if (
      tagName === 'EMPLOYEE_SUPERVISOR' ||
      tagName === 'EMPLOYEE_LOCATION' ||
      tagName === 'EMPLOYEE_ROLE'
    ) {
      return renderSelectInput(tagName, elementId);
    } else {
      return renderTextInput(tagName, elementId);
    }
  };

  const renderDateInput = (tagName: string, elementId: string) => {
    return (
      <DateInput
        id={`${elementId}_${tagName}`}
        type="date"
        label={titleize(tagName)}
        value={inputValues[`${elementId}_${tagName}`] || ''}
        onChange={e => handleChange(elementId, tagName, e.target.value)}
        placeholder="MM/DD/YYYY"
      />
    );
  };

  const renderSelectInput = (tagName: string, elementId: string) => {
    return (
      <div className="flex flex-col gap-[0.625rem] w-full">
        <label
          htmlFor="roleId"
          className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
          {titleize(tagName)}
        </label>
        <CustomSelect
          key={`${elementId}_${tagName}`}
          value={inputValues[`${elementId}_${tagName}`] || ''}
          onChange={selectedOption =>
            handleChange(elementId, tagName, selectedOption.value)
          }
          options={tagOptions[tagName] || []}
        />
      </div>
    );
  };

  const renderTextInput = (tagName: string, elementId: string) => {
    return (
      <TextInput
        id={`${elementId}_${tagName}`}
        type="text"
        label={titleize(tagName)}
        value={inputValues[`${elementId}_${tagName}`] || ''}
        onChange={e => handleChange(elementId, tagName, e.target.value)}
        placeholder={''}
      />
    );
  };

  return (
    <div className="w-full space-y-4">
      {elements.map(element => {
        const content = element.properties.content || '';
        const tags = content.match(/tag-value="\{([^}]+)\}"/g) || [];

        if (tags.length === 0) return null;

        return (
          <div key={element.id} className="space-y-2">
            {tags.map(tag => {
              const tagMatch = tag.match(/tag-value="\{([^}]+)\}"/);
              const tagName = tagMatch ? tagMatch[1] : '';

              return renderInput(tagName, element.id);
            })}
          </div>
        );
      })}
    </div>
  );
};

export default DynamicTextBlockInputs;
